import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import PublicStandings from "@/pages/PublicStandings";
import Bracket from "@/pages/Bracket";

const BASE = process.env.REACT_APP_BACKEND_URL;
const API = `${BASE}/api`;

/**
 * /display — no-auth full-screen public display route.
 *
 * Admin toggles the mode via PUT /api/display/mode. This page polls
 * /api/public/display/state every 2s and swaps the visible sub-view.
 * Intended for use on the venue LED/projector — hide browser chrome via
 * F11 or a kiosk profile.
 *
 * Modes (§14-§25 of the Team Portal spec):
 *   auction    — nominated-player card (photo, rank, cat, bids)
 *   scoreboard — live match scoreboard (both team logos + 3 seed scores)
 *   standings  — Group A/B/C standings tables
 *   bracket    — knockout bracket
 *   lineup     — one team's seed 1/2/3 pairing
 *   idle       — Mara Padel League holding screen
 */
export default function PublicDisplay() {
  const [state, setState] = useState({ mode: "idle", context: {} });
  const nav = useNavigate();

  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/public/display/state`);
        if (!stop) setState(data);
      } catch (_) { /* silent — retry on next tick */ }
    };
    load();
    const t = setInterval(load, 2000);
    return () => { stop = true; clearInterval(t); };
  }, []);

  return (
    <div className="min-h-screen bg-[#060d09] text-white overflow-hidden" data-testid={`display-mode-${state.mode}`}>
      {state.mode === "auction" && <AuctionMode />}
      {state.mode === "scoreboard" && <ScoreboardMode ctx={state.context || {}} />}
      {state.mode === "standings" && <StandingsMode />}
      {state.mode === "bracket" && <BracketMode />}
      {state.mode === "lineup" && <LineupMode ctx={state.context || {}} />}
      {(!state.mode || state.mode === "idle") && <IdleMode />}
    </div>
  );
}

/* -------------------- IDLE / HOLDING -------------------- */
function IdleMode() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-16">
      <img
        src="/mara-logo.png"
        alt="Mara Padel League"
        className="w-64 h-64 object-contain opacity-90 mb-12"
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        data-testid="idle-logo"
      />
      <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Mara Padel League</div>
      <h1 className="font-anton text-8xl md:text-9xl uppercase tracking-tighter mt-4 leading-none">
        SEASON <span className="text-[#00A86B]">3</span>
      </h1>
      <div className="mt-8 text-zinc-500 uppercase tracking-widest text-sm">Kenya · 2026</div>
    </div>
  );
}

/* -------------------- AUCTION MODE -------------------- */
function AuctionMode() {
  const [snap, setSnap] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/auction/eligibility`);
        if (!stop) setSnap(data);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 1500);
    return () => { stop = true; clearInterval(t); };
  }, []);

  const p = snap?.player;
  if (!p) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Live Auction</div>
          <div className="font-anton text-6xl uppercase mt-2">Awaiting nomination…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2 gap-8 p-8 md:p-12">
      {/* LEFT — huge photo */}
      <div className="flex items-center justify-center">
        <div className="relative w-full aspect-[3/4] rounded-2xl overflow-hidden border-4 border-[#F4B400] shadow-2xl">
          {p.photo_url ? (
            <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-[#11241b] flex items-center justify-center">
              <span className="font-anton text-9xl text-white/20">{(p.name || "?").slice(0, 1)}</span>
            </div>
          )}
          <div className="absolute top-4 left-4 bg-[#F4B400] text-black font-bold uppercase tracking-widest text-xs px-3 py-1 rounded-full">
            Up Now
          </div>
        </div>
      </div>

      {/* RIGHT — player details + bid */}
      <div className="flex flex-col justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Mara Padel League · Season 3</div>
          <h1 className="font-anton text-6xl md:text-8xl uppercase tracking-tighter mt-2 leading-none">
            {p.name}
          </h1>
          <div className="flex gap-4 mt-6">
            <div className="p-4 rounded-xl bg-[#11241b] border-2 border-white/10 min-w-[120px]">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Ranking</div>
              <div className="font-anton text-5xl text-white leading-none mt-1">
                {p.current_ranking || p.original_ranking || "—"}
              </div>
            </div>
            <div className="p-4 rounded-xl bg-[#11241b] border-2 border-[#F4B400]/60 min-w-[120px]">
              <div className="text-[10px] uppercase tracking-widest text-[#F4B400]">Category</div>
              <div className="font-anton text-5xl text-[#F4B400] leading-none mt-1">
                {p.category || "—"}
              </div>
            </div>
            {p.level_of_play && (
              <div className="p-4 rounded-xl bg-[#11241b] border-2 border-white/10">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Level</div>
                <div className="font-anton text-2xl text-white mt-2">{p.level_of_play}</div>
              </div>
            )}
          </div>
          {p.achievements && (
            <div className="mt-6 p-4 rounded-xl bg-[#0f1a15] border border-white/10 max-h-64 overflow-hidden">
              <div className="text-[10px] uppercase tracking-widest text-[#00A86B] mb-2">Achievements</div>
              <div className="text-sm text-zinc-300 whitespace-pre-line leading-relaxed line-clamp-8">
                {p.achievements}
              </div>
            </div>
          )}
        </div>
        {/* BID PANEL */}
        <BidPanel />
      </div>
    </div>
  );
}

function BidPanel() {
  const [auc, setAuc] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/public/broadcast/current`);
        if (data.match_id) {/* no-op — we use auction endpoint below */}
      } catch (_) { /* silent */ }
      try {
        const { data } = await axios.get(`${API}/auction`);
        if (!stop) setAuc(data);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 1000);
    return () => { stop = true; clearInterval(t); };
  }, []);
  if (!auc) return null;
  const cur = auc.current_bid_amount || 0;
  const nextBid = cur + 500;
  return (
    <div className="mt-8 grid grid-cols-2 gap-4">
      <div className="p-6 rounded-xl bg-gradient-to-br from-[#00A86B] to-[#00693F] text-white shadow-xl">
        <div className="text-[10px] uppercase tracking-widest opacity-80">Current Bid</div>
        <div className="font-anton text-5xl leading-none mt-1">KSh {cur.toLocaleString()}</div>
      </div>
      <div className="p-6 rounded-xl bg-[#11241b] border-2 border-[#F4B400] text-[#F4B400] shadow-xl">
        <div className="text-[10px] uppercase tracking-widest opacity-80">Next Bid</div>
        <div className="font-anton text-5xl leading-none mt-1">KSh {nextBid.toLocaleString()}</div>
      </div>
      {auc.current_bid_team_id && (
        <div className="col-span-2 p-4 rounded-xl bg-red-600 text-white text-center">
          <div className="text-[10px] uppercase tracking-widest opacity-80">Bidding Team</div>
          <div className="font-anton text-3xl leading-none mt-1">{auc.current_bid_team_name || "—"}</div>
        </div>
      )}
    </div>
  );
}

/* -------------------- SCOREBOARD MODE -------------------- */
function ScoreboardMode({ ctx }) {
  const [match, setMatch] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      try {
        let mid = ctx.match_id;
        if (!mid) {
          const c = await axios.get(`${API}/public/broadcast/current`);
          mid = c.data.match_id;
        }
        if (!mid) return;
        const { data } = await axios.get(`${API}/public/matches/${mid}`);
        if (!stop) setMatch(data);
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 1000);
    return () => { stop = true; clearInterval(t); };
  }, [ctx.match_id]);

  if (!match) {
    return <div className="min-h-screen flex items-center justify-center text-3xl font-anton uppercase">No match selected</div>;
  }

  return (
    <div className="min-h-screen p-8 flex flex-col">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Mara Padel League · Season 3</div>
        <div className="text-sm text-zinc-500 mt-1 uppercase tracking-widest">
          {match.court || ""} · {match.stage || ""}
        </div>
      </div>

      <div className="grid grid-cols-3 items-center gap-6 mb-10">
        <SideBadge team={match.team_a} score={match.score_a} />
        <div className="text-center">
          <div className="font-anton text-5xl uppercase text-zinc-500">VS</div>
          <div className="mt-2 text-[10px] uppercase tracking-widest text-red-500 animate-pulse">
            {match.status === "live" ? "● Live" : match.status}
          </div>
        </div>
        <SideBadge team={match.team_b} score={match.score_b} alignRight />
      </div>

      <div className="flex-1 grid grid-rows-3 gap-4">
        {[1, 2, 3].map((seed) => (
          <SeedRow key={seed} seed={seed} match={match} />
        ))}
      </div>
    </div>
  );
}

function SideBadge({ team, score, alignRight }) {
  return (
    <div className={`flex ${alignRight ? "flex-row-reverse" : "flex-row"} items-center gap-6`}>
      <div className="w-32 h-32 rounded-full bg-[#11241b] border-4 border-white/10 flex items-center justify-center overflow-hidden shrink-0">
        {team?.logo_url ? (
          <img src={team.logo_url} alt={team.name} className="w-full h-full object-contain" />
        ) : (
          <span className="font-anton text-6xl text-white/40">{(team?.name || "?").slice(0, 2).toUpperCase()}</span>
        )}
      </div>
      <div className={alignRight ? "text-right" : "text-left"}>
        <div className="font-anton text-4xl md:text-5xl uppercase text-white leading-none">
          {team?.name || "TBD"}
        </div>
        <div className="font-anton text-8xl text-[#F4B400] leading-none mt-2">
          {score ?? 0}
        </div>
      </div>
    </div>
  );
}

function SeedRow({ seed, match }) {
  const a = (match?.seed_lineup_a || {})[String(seed)] || [];
  const b = (match?.seed_lineup_b || {})[String(seed)] || [];
  return (
    <div className="grid grid-cols-3 items-center gap-4 p-4 rounded-xl bg-[#0f1a15] border border-white/10">
      <div className="text-white text-lg font-bold text-right truncate">
        {a.length ? a.map(p => p.slice(0, 8)).join(" & ") : <span className="text-zinc-600">—</span>}
      </div>
      <div className="text-center">
        <div className="text-[10px] uppercase tracking-widest text-[#F4B400]">Seed {seed}</div>
        <div className="font-anton text-3xl text-white">— · —</div>
      </div>
      <div className="text-white text-lg font-bold truncate">
        {b.length ? b.map(p => p.slice(0, 8)).join(" & ") : <span className="text-zinc-600">—</span>}
      </div>
    </div>
  );
}

/* -------------------- STANDINGS MODE -------------------- */
function StandingsMode() {
  return (
    <div className="min-h-screen p-8">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Mara Padel League · Season 3</div>
        <h1 className="font-anton text-6xl uppercase tracking-tighter mt-2">Standings</h1>
      </div>
      <div className="max-w-6xl mx-auto">
        <PublicStandings />
      </div>
    </div>
  );
}

/* -------------------- BRACKET MODE -------------------- */
function BracketMode() {
  return (
    <div className="min-h-screen p-8">
      <div className="text-center mb-8">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Mara Padel League · Season 3</div>
        <h1 className="font-anton text-6xl uppercase tracking-tighter mt-2">Bracket</h1>
      </div>
      <div className="max-w-7xl mx-auto">
        <Bracket />
      </div>
    </div>
  );
}

/* -------------------- LINEUP MODE -------------------- */
function LineupMode({ ctx }) {
  const [team, setTeam] = useState(null);
  useEffect(() => {
    let stop = false;
    const load = async () => {
      if (!ctx.team_id && !ctx.team_slug) return;
      try {
        const url = ctx.team_slug
          ? `${API}/public/teams/${ctx.team_slug}`
          : `${API}/public/teams`;
        const { data } = await axios.get(url);
        if (!stop) setTeam(ctx.team_slug ? data : (data.find(t => t.id === ctx.team_id) || null));
      } catch (_) { /* silent */ }
    };
    load();
    const t = setInterval(load, 5000);
    return () => { stop = true; clearInterval(t); };
  }, [ctx.team_id, ctx.team_slug]);

  if (!team) {
    return <div className="min-h-screen flex items-center justify-center text-3xl font-anton uppercase">No team selected</div>;
  }

  return (
    <div className="min-h-screen p-8">
      <div className="text-center mb-10">
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#F4B400]">Team Lineup</div>
        <h1 className="font-anton text-7xl uppercase tracking-tighter mt-2" style={{ color: team.brand_color || team.color || "#fff" }}>
          {team.name}
        </h1>
      </div>
      <div className="max-w-4xl mx-auto grid gap-4">
        {(team.players || []).map((p) => (
          <div key={p.id} className="p-4 rounded-xl bg-[#11241b] border border-white/10 flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-black/30 overflow-hidden shrink-0">
              {p.photo_url && <img src={p.photo_url} alt={p.name} className="w-full h-full object-cover" />}
            </div>
            <div className="flex-1">
              <div className="font-anton text-2xl uppercase text-white">{p.name}</div>
              <div className="text-sm text-zinc-500 uppercase tracking-widest">
                {p.category || "—"} · Rank {p.current_ranking || p.original_ranking || "—"}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
